import {
  AsyncFieldError,
  AsyncValidationError,
  BookmarkablePage,
  FormValues,
  getValue,
  InputWizardItemMessage,
  InputWizardPanel,
  InputWizardPanelComponent,
  MessageType,
  ReviewItem,
  SelectOption,
  useNavigation,
} from "o4a-react";
import * as React from "react";
import { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { ExadataDbSystemShapeSelectFields } from "../../components/DbSystemShapeSelect/ExadataDbSystemShapeSelect";
import { DeploymentAsyncValidationPhase } from "../../components/DeploymentPhases/DeploymentAsyncValidationPhase";
import { DeployedService, DeploymentCreatePhase } from "../../components/DeploymentPhases/DeploymentCreationPhase";
import { ConsoleContext } from "../../console/ConsoleContext";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import {
  EXADATA_INFRAS_RESOURCE_TYPE_PATH,
  EXAINFRA_CREATE_ROUTE,
  PageId,
  PageRegistrationConfig,
} from "../../constants/pluginConstants";
import {
  CreateCloudExadataInfrastructureDeploymentDetails,
  ExadataDeploymentParametersDetails,
} from "../../gen/clients/mchub-azure-api-client";
import {
  DeploymentKinds,
  getResourceTypeMessage,
  getTagsPerResourceTypeMap,
  ResourceType,
} from "../../helpers/resourceHelper";
import { getTimestamp } from "../../helpers/timeHelper";
import { NavigationAnalyticsData, useAnalytics } from "../../hooks/useAnalytics";
import { useFeatures } from "../../hooks/useFeatures";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  EXAINFRA_DEPLOYMENT_CREATE_POLL_DELAY,
  EXAINFRA_DEPLOYMENT_CREATE_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { ReviewTabContent } from "../CreateCommon/ReviewTabContent";
import { Fields as TagsFields, GROUP_TAGS, TagsTabContent } from "../CreateCommon/TagsTabContent";
import { BasicsTabComponent, BasicsTabContent, Fields as BasicsFields, GROUP_BASICS } from "./BasicsTabContent";
import { ConfigTabContent, Fields as ConfigFields, GROUP_CONFIG } from "./ConfigTabContent";

const registrationIds = PageRegistrationConfig[PageId.EXAINFRA_CREATE].map(config => config.key);

const onProcessAsyncValidationErrors = (
  asyncErrors: AsyncValidationError[],
): AsyncValidationError[] => asyncErrors.map(asyncError => {
  if (typeof asyncError === "string") {
    return asyncError;
  }
  const asyncFieldError = asyncError as AsyncFieldError;
  if (asyncFieldError.field === "cloudExadataInfrastructureDetails.computeCount"
   || asyncFieldError.field === "cloudExadataInfrastructureDetails.storageCount") {
    asyncFieldError.field = ConfigFields.ExadataSystemModel;
  }

  return asyncFieldError;
});

enum CreationPhase {
  ASYNC_VALIDATE = "ASYNC_VALIDATE",
  CREATE_SUBMIT = "CREATE_SUBMIT",
}

export const ExaInfraCreatePage = (): JSX.Element => {
  const { trackActionDiscard } = useAnalytics();

  const { back, navigateToSelf, customData } = useNavigation(ConsoleContext);

  const { preferredLocationPerSubscription, preferredSubscription } = React.useContext<Settings>(SettingsContext);
  const preferredLocation = preferredLocationPerSubscription[preferredSubscription] || "";

  const [subscriptionId, setSubscriptionId] = React.useState<string>("");
  const [resourceGroupName, setResourceGroupName] = React.useState<string>("");
  const [location, setLocation] = React.useState<string>("");
  const { enableExaInfraPreflight } = useFeatures();

  const resourceNameRef = React.useRef<string>(""); // to hold the name to be used in failure message
  const deploymentNameRef = React.useRef<string>("");

  React.useEffect(() => {
    setLocation(preferredLocation);
    setSubscriptionId(preferredSubscription);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // In case page was navigated to directly by entering its URL in the browser
    navigateToSelf(EXAINFRA_CREATE_ROUTE, registrationIds[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [panelRef, setPanelRef] = React.useState<InputWizardPanelComponent>({} as InputWizardPanelComponent);

  const basicsTabRef = React.useRef<BasicsTabComponent>();
  const setBasicsTabRef = (basicTab: BasicsTabComponent): void => {
    basicsTabRef.current = basicTab;
  };
  const [creationPhase, setCreationPhase] = React.useState<CreationPhase | undefined>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [createExaInfraPayload, setCreateExaInfraPayload] = React.useState<any>();
  const asyncValidationCallbacks = React.useRef<{
    resolve:(errors?: AsyncValidationError[]) => void,
    reject: (errorMessage: string) => void,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      }>({ resolve: () => {}, reject: () => {} });
  const submitCallbacks = React.useRef<{
        reject:(errorMessage: string, validationErrors?: AsyncValidationError[]) => void
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          }>({ reject: () => {} });

  const onClose = (): void => back();
  const onAsyncValidate = (
    formValues: FormValues,
    resolve: (errors?: AsyncValidationError[]) => void,
    reject: (errorMessage: string) => void,
  ): void => {
    setCreateExaInfraPayload(buildCreateExaInfraPayload(formValues));
    asyncValidationCallbacks.current = { resolve, reject };
    setCreationPhase(CreationPhase.ASYNC_VALIDATE);
  };

  const onSubmit = (formValues: FormValues): void => {
    setCreateExaInfraPayload(buildCreateExaInfraPayload(formValues));
    setCreationPhase(CreationPhase.CREATE_SUBMIT);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const buildCreateExaInfraPayload = (formValues: FormValues): any => {
    resourceNameRef.current = getValue(formValues, BasicsFields.Name, GROUP_BASICS);
    const name = getValue(formValues, BasicsFields.Name, GROUP_BASICS);
    const computeCount = getValue(formValues, ExadataDbSystemShapeSelectFields.DatabaseServers, GROUP_CONFIG);
    const storageCount = getValue(formValues, ExadataDbSystemShapeSelectFields.StorageServers, GROUP_CONFIG);
    const tags = getValue(formValues, TagsFields.tags, GROUP_TAGS);
    const tagsMap = getTagsPerResourceTypeMap(tags);

    const cloudExadataInfrastructureDetails: CreateCloudExadataInfrastructureDeploymentDetails = {
      kind: DeploymentKinds.Create,
      name,
      computeCount,
      storageCount,
      shape: getValue(formValues, ConfigFields.ExadataSystemModel, GROUP_CONFIG)?.[0].id,
      maintenanceWindow: { preference: "NO_PREFERENCE" },
      freeformTags: tagsMap[ResourceType.INFRA],
    };
    deploymentNameRef.current = `${EXADATA_INFRAS_RESOURCE_TYPE_PATH}-${getTimestamp()}`;
    const createDeploymentDetails = {
      name: deploymentNameRef.current,
      parameters: {
        kind: DeploymentKinds.Exadata,
        cloudExadataInfrastructureDetails,
      } as ExadataDeploymentParametersDetails,
    };

    const payload = ({
      subscriptionId: getValue(formValues, BasicsFields.Subscription, GROUP_BASICS)?.[0].id,
      resourceGroupName: getValue(formValues, BasicsFields.ResourceGroup, GROUP_BASICS)?.[0].text,
      apiVersion: MultiCloudDatabaseApiVersion,
      createDeploymentDetails,
    });
    return payload;
  };

  const buildBasicInfo = (formValues: FormValues): ReviewItem[] => {
    const reviewBasicInfo = [
      {
        label: Messages.labels.subscription(),
        value: getValue(formValues, BasicsFields.Subscription, GROUP_BASICS)?.[0].text,
      },
      {
        label: Messages.labels.resourceGroup(),
        value: getValue(formValues, BasicsFields.ResourceGroup, GROUP_BASICS)?.[0].text,
      },
      {
        label: Messages.labels.name(),
        value: getValue(formValues, BasicsFields.Name, GROUP_BASICS),
      },
      {
        label: Messages.labels.region(),
        value: getValue(formValues, BasicsFields.Region, GROUP_BASICS)?.[0].text,

      },
    ];
    return reviewBasicInfo;
  };

  const buildConfigInfo = (formValues: FormValues): ReviewItem[] => {
    const databaseServers = getValue(formValues, ExadataDbSystemShapeSelectFields.DatabaseServers, GROUP_CONFIG);
    const storageServers = getValue(formValues, ExadataDbSystemShapeSelectFields.StorageServers, GROUP_CONFIG);
    const reviewConfigInfo = [
      {
        label: Messages.labels.exadataSystemModel(),
        value: getValue(formValues, ConfigFields.ExadataSystemModel, GROUP_CONFIG)?.[0].text,
      },
    ];
    if (databaseServers > 0) {
      reviewConfigInfo.push(
        {
          label: Messages.labels.databaseServers(),
          value: databaseServers,
        },
      );
    }
    if (storageServers > 0) {
      reviewConfigInfo.push({ label: Messages.labels.storageServers(), value: storageServers });
    }

    return reviewConfigInfo;
  };

  const buildTagsInfo = (formValues: FormValues): ReviewItem[] => {
    const tagsInfo = getValue(formValues, TagsFields.tags, GROUP_TAGS);
    const tags: { label: string; value: string; }[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tagsInfo.forEach((element: any) => (
      element.resources.forEach((resource: { [key: string]: string }) => (
        element.name !== undefined && tags.push({
          label: element.name,
          value: `${element.value !== undefined ? element.value : ""}(${resource.text})`,
        })
      ))));
    if (tags.length === 0) tags.push({ label: "None", value: "" });
    tags.sort((a, b) => ((a.value.split("(")[1].split(")")[0] > b.value.split("(")[1].split(")")[0]) ? 1 : -1));
    return tags;
  };

  const getTagsResourceTypeOptions = (): SelectOption[] => {
    const resourceOptions: SelectOption[] = [
      { id: ResourceType.INFRA, text: getResourceTypeMessage(ResourceType.INFRA) }];

    return resourceOptions;
  };

  const onRenderReview = (formValues: FormValues): JSX.Element => {
    const reviewSections = [];
    reviewSections.push(
      { title: Messages.createInfra.basicsTab.title(), items: buildBasicInfo(formValues) },
      { title: Messages.createExaDb.configurationTab.title(), items: buildConfigInfo(formValues) },
      { title: Messages.tags.title(), items: buildTagsInfo(formValues) },
    );
    return <ReviewTabContent reviewSections={reviewSections} />;
  };

  const wizardItems = [];
  wizardItems.push({
    header: Messages.createInfra.basicsTab.title(),
    groupName: GROUP_BASICS,
    content: (
      <BasicsTabContent
        inputWizardPanelRef={panelRef}
        componentRef={setBasicsTabRef}
        subscription={subscriptionId}
        location={location}
      />
    ),
    onUnselecting: () => basicsTabRef.current?.closeSidePanels(),
  });
  wizardItems.push({
    header: Messages.createInfra.configurationTab.title(),
    groupName: GROUP_CONFIG,
    content: (
      <ConfigTabContent
        inputWizardPanelRef={panelRef}
        subscriptionId={subscriptionId}
        location={location}
      />
    ),
  });
  wizardItems.push({
    header: Messages.tags.title(),
    groupName: GROUP_TAGS,
    content: <TagsTabContent resourceOptions={getTagsResourceTypeOptions()} />,
  });

  return (
    <BookmarkablePage
      appContext={ConsoleContext}
      registrationIds={registrationIds}
      title={Messages.createInfra.titles.short()}
    >
      <InputWizardPanel
        componentRef={setPanelRef}
        title={Messages.createInfra.titles.long()}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onFormChange={(_formValues: FormValues, fieldValue: any, field: string) => {
          if (field === BasicsFields.Subscription) {
            setSubscriptionId(fieldValue?.[0]?.id);
          } else if (field === BasicsFields.ResourceGroup) {
            setResourceGroupName(fieldValue?.[0]?.text);
          } else if (field === BasicsFields.Region) {
            setLocation(fieldValue?.[0]?.id);
          }
        }}
        items={wizardItems}
        itemsMessages={[{
          itemNdx: 0,
          type: MessageType.WARNING,
          message: Messages.createCommon.dependencyWarnings.basicsChangeWarning(Messages.labels.exadataInfra()),
          hideOnFirstSelect: true,
        }] as InputWizardItemMessage[]}
        onRenderReview={onRenderReview}
        onUnselectingReview={() => {
          // Reset to cover the case of the user navigating away from the review tab before the results come back
          setCreateExaInfraPayload(undefined);
          setCreationPhase(undefined);
        }}
        onAsyncValidate={enableExaInfraPreflight ? onAsyncValidate : undefined}
        onSubmit={(
          formValues: FormValues,
          reject: (errorMessage: string, validationErrors?: AsyncValidationError[]) => void,
        ) => {
          submitCallbacks.current = { reject };
          onSubmit(formValues);
        }}
        onClose={() => {
          const analytics = customData?.analytics as NavigationAnalyticsData;
          if (analytics) {
            trackActionDiscard(analytics.actionName, analytics.pageId, analytics.panelId);
          }
          onClose();
        }}
      />
      {creationPhase === CreationPhase.CREATE_SUBMIT && (
        <DeploymentCreatePhase
          panelRef={panelRef}
          deployedService={DeployedService.EXADATA_INFRA}
          subscriptionId={subscriptionId}
          resourceGroupName={resourceGroupName}
          location={location}
          resourceName={resourceNameRef.current}
          deploymentName={deploymentNameRef.current}
          submitPayload={createExaInfraPayload}
          submitReject={submitCallbacks.current.reject}
          onProcessAsyncValidationErrors={onProcessAsyncValidationErrors}
          asyncNotification={{
            methodKey: AsyncNotificationMethodKey.DEPLOYMENT_GET,
            delay: EXAINFRA_DEPLOYMENT_CREATE_POLL_DELAY,
            pollingInterval: EXAINFRA_DEPLOYMENT_CREATE_POLL_INTERVAL,
            polledResponseKey: AsyncNotificationPolledResponseKey.EXAINFRA_DEPLOYMENT_CREATED_CHECK,
          }}
          onPostSubmit={() => {
            setCreateExaInfraPayload(undefined);
            setCreationPhase(undefined);
          }}
        />
      )}
      {creationPhase === CreationPhase.ASYNC_VALIDATE && (
        <DeploymentAsyncValidationPhase
          location={location}
          asyncValidatePayload={createExaInfraPayload}
          asyncValidateResolve={asyncValidationCallbacks.current.resolve}
          asyncValidateReject={asyncValidationCallbacks.current.reject}
          onProcessAsyncValidationErrors={onProcessAsyncValidationErrors}
          onPostAsyncValidate={() => {
            setCreateExaInfraPayload(undefined);
            setCreationPhase(undefined);
          }}
        />
      )}
    </BookmarkablePage>
  );
};
